import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { CompanyLogo, getDomainFromCompany } from "../../../components/ui/CompanyLogo";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import {
  queryClient,
  useAllIntegrations,
  useCurrentUser,
  useHubspotIntegration,
  useMeetingNoteTakerPreference,
  useSalesforceIntegration,
  useWorkspaceMembers,
  useWorkspaceObjectsDetailed,
} from "../../../hooks/QueryHooks";
import { cn } from "../../../lib/utils";
import { createIntegration, getAllIntegration } from "../../../services/integrationEndpoint";
import { createWorkspaceObject } from "../../../services/workspace/workspaceEndpoints";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { PersonalConnectorsTab } from "./integrations/PersonalConnectorsTab";

type Category = "All" | "CRM" | "CDP" | "Communications" | "Notifications";

type IntegrationCard = {
  id: string;
  name: string;
  description: string;
  category: Exclude<Category, "All">;
  connected: boolean;
  pending: boolean;
  scope: "Workspace" | "Member";
  onClick: () => Promise<void> | void;
  isPrimary?: boolean;
};

const CATEGORIES: readonly Category[] = ["All", "CRM", "CDP", "Communications", "Notifications"];

const IntegrationsPage = () => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<Category>("All");

  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const { data: allIntegrations }: any = useAllIntegrations({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: workspaceObjectsDetailed }: any = useWorkspaceObjectsDetailed({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });
  const existingObjects = workspaceObjectsDetailed || [];

  const navigate = useNavigate();
  const user: any = useCurrentUser({});

  const { data: workspaceMembersData }: any = useWorkspaceMembers({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });
  const workspaceMembers = useMemo(
    () => workspaceMembersData?.members || workspaceMembersData?.users || [],
    [workspaceMembersData],
  );

  const currentUserEmail = user?.data?.email;
  const currentWorkspaceMember = useMemo(() => {
    if (!currentUserEmail) return null;
    const needle = String(currentUserEmail).toLowerCase().trim();
    return workspaceMembers.find((member: any) => String(member?.email || "").toLowerCase().trim() === needle) || null;
  }, [workspaceMembers, currentUserEmail]);
  const currentWorkspaceMemberId = currentWorkspaceMember?.id;

  const getTopLevelIntegration = (sourceName: string) => (allIntegrations || []).find((item: any) => {
    if (item?.source_name !== sourceName) return false;
    const isMemberLevel = item?.is_member_level ?? item?.isMemberLevel ?? item?.member_level ?? item?.memberLevel;
    return !isMemberLevel;
  });

  // Member-scoped connectors (Gmail / Google Calendar) are created at the member
  // level by the backend (is_member_level: true), so they must NOT be filtered out
  // the way workspace-level integrations are. Prefer an integration that already
  // holds a member_integration for the current member; otherwise fall back to the
  // first integration matching the source so the connect flow can reuse it.
  const getMemberSourceIntegration = (sourceName: string) => {
    const matches = (allIntegrations || []).filter((item: any) => item?.source_name === sourceName);
    const withCurrentMember = matches.find((item: any) => {
      const memberIntegrations = item?.member_integrations || item?.memberIntegrations || [];
      return memberIntegrations.some(isMemberIntegrationForCurrentMember);
    });
    return withCurrentMember || matches[0] || undefined;
  };

  const isMemberIntegrationForCurrentMember = (memberIntegration: any) => {
    const memberIntegrationId = memberIntegration?.workspace_member_id
      ?? memberIntegration?.workspaceMemberId
      ?? memberIntegration?.workspace_member?.id;

    if (currentWorkspaceMemberId && memberIntegrationId === currentWorkspaceMemberId) return true;
    if (!currentUserEmail) return false;

    const email = String(currentUserEmail).toLowerCase().trim();
    return (
      String(memberIntegration?.primary_email || "").toLowerCase().trim() === email
      || String(memberIntegration?.external_account_id || "").toLowerCase().trim() === email
    );
  };

  const gmailTopLevelIntegration = useMemo(
    () => getMemberSourceIntegration("gmail"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allIntegrations, currentWorkspaceMemberId, currentUserEmail],
  );

  const googleCalendarTopLevelIntegration = useMemo(
    () => getMemberSourceIntegration("google-calendar"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allIntegrations, currentWorkspaceMemberId, currentUserEmail],
  );

  const gmailMemberIntegrationForMe = useMemo(() => {
    const memberIntegrations = gmailTopLevelIntegration?.member_integrations || gmailTopLevelIntegration?.memberIntegrations || [];
    return memberIntegrations.find(isMemberIntegrationForCurrentMember) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmailTopLevelIntegration, currentWorkspaceMemberId, currentUserEmail]);

  const googleCalendarMemberIntegrationForMe = useMemo(() => {
    const memberIntegrations = googleCalendarTopLevelIntegration?.member_integrations || googleCalendarTopLevelIntegration?.memberIntegrations || [];
    return memberIntegrations.find(isMemberIntegrationForCurrentMember) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleCalendarTopLevelIntegration, currentWorkspaceMemberId, currentUserEmail]);

  const slackTopLevelIntegration = useMemo(
    () => getTopLevelIntegration("slack"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allIntegrations],
  );

  const slackIsConnected = Boolean(
    slackTopLevelIntegration?.setup_complete
    && (slackTopLevelIntegration?.credential_status_ok ?? true),
  );
  const slackIsPending = Boolean(slackTopLevelIntegration && !slackTopLevelIntegration?.setup_complete);

  const { data: hubspotIntegration }: any = useHubspotIntegration({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: meetingNoteTakerPreference } = useMeetingNoteTakerPreference({
    workspaceId: workspace?.id,
  });

  const { data: salesforceIntegration }: any = useSalesforceIntegration({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const segmentIntegration = useMemo(
    () => allIntegrations?.filter((item: any) => item?.source_name === "segment_api"),
    [allIntegrations],
  );

  const nativeIntegration = useMemo(
    () => allIntegrations?.filter((item: any) => item?.source_name === "native"),
    [allIntegrations],
  );

  const apiClick = async () => {
    if (!nativeIntegration?.length) {
      try {
        await createIntegration({
          workspace_identifier: workspace.unique_identifier,
          source_name: "native",
          workspace_id: workspace.id,
          created_by_user_id: user.data.user_id,
          is_active: true,
        });
        queryClient.invalidateQueries(["integrations"]);
      } catch {
        return;
      }
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations/sdk-setup`);
  };

  const segmentClick = async () => {
    if (!segmentIntegration?.length) {
      try {
        await createIntegration({
          workspace_identifier: workspace.unique_identifier,
          source_name: "segment_api",
          workspace_id: workspace.id,
          created_by_user_id: user.data.user_id,
          is_active: true,
        });
        queryClient.invalidateQueries(["integrations"]);
      } catch {
        return;
      }
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations/segment-setup`);
  };

  const gmailClick = async () => {
    if (!workspace?.id || !workspace?.unique_identifier || !user?.data?.user_id) {
      return;
    }

    try {
      let topIntegration: any = gmailTopLevelIntegration;

      if (!topIntegration?.id) {
        try {
          const created = await createIntegration({
            workspace_identifier: workspace.unique_identifier,
            source_name: "gmail",
            workspace_id: workspace.id,
            created_by_user_id: user.data.user_id,
            is_active: true,
          });
          topIntegration = created?.data;
          queryClient.invalidateQueries(["integrations"]);
        } catch {
          // best effort
        }
      }

      if (!topIntegration?.id) {
        try {
          const refreshed = await getAllIntegration({ workspace_id: workspace.id });
          topIntegration = (refreshed?.data || []).find((item: any) => item?.source_name === "gmail");
        } catch {
          // best effort
        }
      }
    } finally {
      try {
        const objectsToCheck = [
          {
            api_slug: "conversations",
            singular_noun: "conversation",
            plural_noun: "Conversations",
            description: "Email threads and conversation containers",
          },
          {
            api_slug: "conversation_messages",
            singular_noun: "conversation_message",
            plural_noun: "Conversation Messages",
            description: "Messages within a conversation",
          },
        ];

        const existingSlugs = new Set(existingObjects.map((object: any) => object.api_slug));

        await Promise.all(objectsToCheck.map(async (objectDefinition) => {
          if (!existingSlugs.has(objectDefinition.api_slug)) {
            await createWorkspaceObject({
              workspace_id: workspace.id,
              data: {
                ...objectDefinition,
                workspace_id: workspace.id,
                table_name: objectDefinition.api_slug,
                enabled: true,
                is_system_object: false,
              },
            });
          }
        }));
      } catch {
        // ignore setup errors
      }

      navigate(`/home/${workspace.unique_identifier}/settings/integrations/gmail`);
    }
  };

  const googleCalendarClick = async () => {
    if (!workspace?.id || !workspace?.unique_identifier || !user?.data?.user_id) {
      return;
    }

    try {
      let topIntegration: any = googleCalendarTopLevelIntegration;

      if (!topIntegration?.id) {
        try {
          const created = await createIntegration({
            workspace_identifier: workspace.unique_identifier,
            source_name: "google-calendar",
            workspace_id: workspace.id,
            created_by_user_id: user.data.user_id,
            is_active: true,
          });
          topIntegration = created?.data;
          queryClient.invalidateQueries(["integrations"]);
        } catch {
          // best effort
        }
      }

      if (!topIntegration?.id) {
        try {
          const refreshed = await getAllIntegration({ workspace_id: workspace.id });
          topIntegration = (refreshed?.data || []).find((item: any) => item?.source_name === "google-calendar");
        } catch {
          // best effort
        }
      }
    } finally {
      try {
        const existingSlugs = new Set(existingObjects.map((object: any) => object.api_slug));
        if (!existingSlugs.has("meetings")) {
          await createWorkspaceObject({
            workspace_id: workspace.id,
            data: {
              api_slug: "meetings",
              singular_noun: "meeting",
              plural_noun: "Meetings",
              description: "Calendar meetings and events",
              workspace_id: workspace.id,
              table_name: "meetings",
              enabled: true,
              is_system_object: false,
            },
          });
        }
      } catch {
        // ignore setup errors
      }

      navigate(`/home/${workspace.unique_identifier}/settings/integrations/google-calendar`);
    }
  };

  const hubspotClick = async () => {
    if (!hubspotIntegration?.length) {
      try {
        await createIntegration({
          workspace_identifier: workspace.unique_identifier,
          source_name: "hubspot",
          workspace_id: workspace.id,
          created_by_user_id: user.data.user_id,
          is_active: true,
        });
        queryClient.invalidateQueries(["integrations"]);
        queryClient.invalidateQueries(["hubspot-integration"]);
      } catch {
        return;
      }
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations/hubspot`);
  };

  const salesforceClick = async () => {
    if (!salesforceIntegration?.length) {
      try {
        await createIntegration({
          workspace_identifier: workspace.unique_identifier,
          source_name: "salesforce",
          workspace_id: workspace.id,
          created_by_user_id: user.data.user_id,
          is_active: true,
        });
        queryClient.invalidateQueries(["integrations"]);
        queryClient.invalidateQueries(["salesforce-integration"]);
      } catch {
        return;
      }
    }
    navigate(`/home/${workspace.unique_identifier}/settings/integrations/salesforce`);
  };

  const allCards = useMemo<IntegrationCard[]>(() => [
    {
      id: "hyperengage-sdk",
      name: "Hyperengage SDK",
      description: "Install the tracking script to send events, identify accounts and users",
      category: "CDP",
      connected: Boolean(nativeIntegration?.length && nativeIntegration[0]?.setup_complete),
      pending: Boolean(nativeIntegration?.length && !nativeIntegration[0]?.setup_complete),
      scope: "Workspace",
      onClick: apiClick,
      isPrimary: true,
    },
    {
      id: "segment",
      name: "Segment",
      description: "Install Hyperengage via Segment's client-side analytics library",
      category: "CDP",
      connected: Boolean(segmentIntegration?.length && segmentIntegration[0]?.setup_complete),
      pending: Boolean(segmentIntegration?.length && !segmentIntegration[0]?.setup_complete),
      scope: "Workspace",
      onClick: segmentClick,
    },
    {
      id: "slack",
      name: "Slack",
      description: "Receive immediate notifications when users take action in your product",
      category: "Communications",
      connected: slackIsConnected,
      pending: slackIsPending,
      scope: "Workspace",
      onClick: () => navigate("slack"),
    },
    {
      id: "gmail",
      name: "Gmail",
      description: "Sync email conversations for AI insights and conversation tracking",
      category: "Communications",
      connected: Boolean(gmailMemberIntegrationForMe),
      pending: false,
      scope: "Member",
      onClick: gmailClick,
    },
    {
      id: "google-calendar",
      name: "Google Calendar",
      description: "Sync meetings for AI-powered meeting context and timelines",
      category: "Communications",
      connected: Boolean(googleCalendarMemberIntegrationForMe),
      pending: false,
      scope: "Member",
      onClick: googleCalendarClick,
    },
    {
      id: "hubspot",
      name: "HubSpot",
      description: "Sync contacts, companies, and deals bi-directionally",
      category: "CRM",
      connected: Boolean(hubspotIntegration?.length && hubspotIntegration[0]?.setup_complete),
      pending: Boolean(hubspotIntegration?.length && !hubspotIntegration[0]?.setup_complete),
      scope: "Workspace",
      onClick: hubspotClick,
    },
    {
      id: "salesforce",
      name: "Salesforce",
      description: "Sync accounts, contacts, and opportunities with Salesforce",
      category: "CRM",
      connected: Boolean(salesforceIntegration?.length && salesforceIntegration[0]?.setup_complete),
      pending: Boolean(salesforceIntegration?.length && !salesforceIntegration[0]?.setup_complete),
      scope: "Workspace",
      onClick: salesforceClick,
    },
    {
      id: "meeting-note-taker",
      name: "Call Intelligence",
      description: "Automatically join and record meetings via Recall.ai",
      category: "Communications",
      connected: Boolean((meetingNoteTakerPreference as any)?.enabled),
      pending: false,
      scope: "Workspace",
      onClick: () => navigate("meeting-note-taker"),
    },
  ], [
    nativeIntegration,
    segmentIntegration,
    slackIsConnected,
    slackIsPending,
    gmailMemberIntegrationForMe,
    googleCalendarMemberIntegrationForMe,
    hubspotIntegration,
    salesforceIntegration,
    meetingNoteTakerPreference,
  ]);

  const primaryCard = useMemo(
    () => allCards.find((card) => card.isPrimary),
    [allCards],
  );

  const filteredCards = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return allCards
      .filter((card) => !card.isPrimary)
      .filter((card) => {
        const categoryMatches = category === "All" || card.category === category;
        const searchMatches = !normalizedSearch
          || card.name.toLowerCase().includes(normalizedSearch)
          || card.description.toLowerCase().includes(normalizedSearch);

        return categoryMatches && searchMatches;
      });
  }, [allCards, category, search]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-[15px] font-semibold text-foreground">Integrations</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">Connect your tools to sync data and automate workflows</p>
      </div>

      <Tabs defaultValue="workspace">
        <TabsList className="mb-4">
          <TabsTrigger value="workspace">Workspace</TabsTrigger>
          <TabsTrigger value="personal">Personal</TabsTrigger>
        </TabsList>

        <TabsContent value="workspace">
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-56 max-w-full">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search integrations..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="h-7 pl-8 text-[13px]"
                />
              </div>

              <Select value={category} onValueChange={(value) => setCategory(value as Category)}>
                <SelectTrigger className="h-7 w-[180px] text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((item) => (
                    <SelectItem key={item} value={item} className="text-[12px]">
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {primaryCard ? (
              <div
                className="flex cursor-pointer items-center gap-4 rounded-lg border border-primary/20 bg-primary/[0.03] p-4 transition-colors hover:bg-primary/[0.06]"
                onClick={() => {
                  primaryCard.onClick();
                }}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">H</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-foreground">{primaryCard.name}</span>
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">Setup</span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">{primaryCard.description}</p>
                </div>
                <Button variant="outline" size="sm" className="h-7 flex-shrink-0 text-[12px]">
                  Configure
                </Button>
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredCards.map((card) => {
                const buttonLabel = card.connected
                  ? "Open"
                  : card.pending
                    ? "Continue"
                    : card.id === "segment"
                      ? "Setup"
                      : "Connect";

                return (
                  <div key={card.id} className="flex flex-col gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-muted">
                    <div className="flex items-start gap-3">
                      <CompanyLogo
                        domain={getDomainFromCompany(card.name === "Call Intelligence" ? "Recall" : card.name)}
                        name={card.name}
                        className="h-8 w-8"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-[13px] font-medium text-foreground">{card.name}</span>
                          {card.connected ? <span className="h-1.5 w-1.5 rounded-full bg-status-active" /> : null}
                        </div>
                        <p className="mt-0.5 line-clamp-2 text-[12px] leading-[16px] text-muted-foreground">
                          {card.description}
                        </p>
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-7 w-fit text-[12px]",
                        card.pending ? "border-status-in-progress/30 text-status-in-progress hover:bg-status-in-progress/[0.06]" : "",
                      )}
                      onClick={() => {
                        card.onClick();
                      }}
                    >
                      {buttonLabel}
                    </Button>
                  </div>
                );
              })}
            </div>

            {filteredCards.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-[13px] text-muted-foreground">No integrations found</p>
              </div>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="personal">
          <div className="space-y-4">
            <p className="text-[13px] text-muted-foreground">
              Personal connectors give the AI agent access to your accounts. Only visible to you.
            </p>
            <PersonalConnectorsTab workspaceId={workspace?.id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationsPage;
